const en = {
  app: {
    name: "Firstier Admin",
  },
  shell: {
    groupOverview: "Overview",
    groupData: "Website data",
    groupOnboarding: "Onboarding",
    groupGrowth: "Growth",
    groupPlayground: "Playground",
    groupAdmin: "Admin",
    nav: {
      dashboard: "Dashboard",
      customers: "Customers",
      users: "Users",
      revenue: "Revenue",
      usage: "Usage",
      agents: "Agents",
      newsletter: "Newsletter",
      applications: "Applications",
      leads: "Leads",
      gated: "Gated Content",
      downloadNotify: "Download notify",
      helpFeedback: "Help feedback",
      invites: "Pending Invites",
      accessRequests: "Access Requests",
      signupCodes: "Signup Codes",
      seo: "SEO",
      keywords: "Keywords",
      questions: "Questions",
      reviewHub: "Review Hub",
      members: "Members",
      settings: "Settings",
      dataTypes: "Data Types",
    },
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    expandNav: "Open navigation",
    toggleTheme: "Toggle theme",
    signOut: "Sign out",
    accountMenu: "Account menu",
    searchPlaceholder: "Search",
    commandMenu: {
      navigation: "Navigation",
    },
  },
  common: {
    unnamed: "User",
    yes: "Yes",
    no: "No",
  },
  auth: {
    title: "Firstier Admin",
    subtitle: "Internal admin access. Staff only.",
    emailLabel: "Email",
    emailPlaceholder: "you@firstier.ai",
    passwordLabel: "Password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    signedOut: "You have been signed out.",
    notAuthorizedTitle: "Not authorized",
    notAuthorizedBody: "This account is not allowed into the console. Sign in with your Firstier staff account.",
    genericError: "Could not sign in. Check your credentials and try again.",
    forgotPassword: "Forgot password?",
    forgotSubtitle: "Enter your email and we'll send a reset link.",
    sendResetLink: "Send reset link",
    resetSent: "If an account exists for that email, a reset link is on its way.",
    backToSignIn: "Back to sign in",
    resetTitle: "Choose a new password",
    resetInvalid: "This reset link is invalid or has expired.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords do not match.",
    setNewPassword: "Set new password",
    resetUpdated: "Password updated.",
    resetUpdateError: "Could not update the password. Try the reset link again.",
  },
  table: {
    empty: "Nothing here yet.",
    error: "Could not load data.",
    emptyTitle: "No data yet",
    emptyDesc: "There is nothing to show here yet. New entries will appear automatically.",
    truncatedWarning: "Very large dataset — only the first 20,000 rows are loaded; older rows are not shown.",
    noResultsTitle: "No matches found",
    noResultsDesc: "No rows match your search or filters. Try adjusting them.",
  },
  dash: {
    inbound30d: "Inbound — last 30 days",
    appsByStatus: "Applications by status",
    customersByPlan: "Customers by plan",
  },
  agents: {
    env: { staging: "Staging data", production: "Production data" },
    killStatus: "Kill switch",
    killActive: "Kill active",
    killInactive: "Running",
    epoch: "Epoch {{epoch}}",
    killReason: "Reason: {{reason}}",
    budget: {
      title: "Budget {{month}}",
      cap: "Cap {{cap}} / month",
      estimated: "Estimated (settled + reserved)",
      invoiced: "Invoiced",
      allowance: "Includes the {{amount}} variable-infra allowance line.",
    },
    leases: "Lease acknowledgements",
    lease: { holder: "Holder", ack: "Status", epoch: "Epoch seen", lastAck: "Last ack" },
    ack: {
      "acknowledged-stop": "Acknowledged stop",
      "unknown-unreachable": "Unknown / unreachable",
      running: "Running",
    },
    registry: "Agent registry",
    runs24h: "Runs, last 24 h",
    col: { agent: "Agent", version: "Version", org: "Organization", state: "State", lastRun: "Last run", started: "Started", cost: "Cost" },
    agentState: { active: "Active", paused: "Paused", disabled: "Disabled" },
    runState: { queued: "Queued", running: "Running", succeeded: "Succeeded", failed: "Failed", killed: "Killed" },
    pause: "Pause",
    resume: "Resume",
    cancel: "Cancel",
    paused: "{{name}} paused.",
    resumed: "{{name}} resumed.",
    pauseDialog: {
      title: "Pause {{name}}?",
      description: "The agent takes no new work until it is resumed. Runs already in flight are not interrupted.",
      reason: "Reason (optional)",
    },
    kill: {
      button: "Kill",
      title: "Kill all agents?",
      description: "Bumps the kill epoch. Every agent and the local fleet lease refuse new work until the kill is cleared out of band.",
      reason: "Reason (required)",
      confirm: "Kill all agents",
      done: "Kill epoch bumped.",
    },
    notReady: "The agent platform schema is not live yet.",
    actionError: "The action failed. Nothing was changed.",
  },
  usage: {
    metric: {
      users_total: "Users",
      sessions_valid: "Sessions (valid)",
      web_online_now: "Web online now",
      desktop_online_now: "Desktop online now",
      web_active_24h: "Web active (24h)",
      ext_active_24h: "Extension active (24h)",
    },
    funnel: {
      users_total: "Users",
      ext_connected: "Extension connected",
      ext_active_7d: "Extension active (7d)",
      desktop_enrolled: "Desktop enrolled",
      desktop_active_24h: "Desktop active (24h)",
    },
    col: {
      org: "Organization",
      users_total: "Users",
      sessions_valid: "Sessions",
      web_active_24h: "Web 24h",
      web_active_7d: "Web 7d",
      web_online_now: "Web now",
      ext_connected: "Ext connected",
      ext_active_24h: "Ext 24h",
      ext_active_7d: "Ext 7d",
      desktop_enrolled: "Desktop enrolled",
      desktop_active_24h: "Desktop 24h",
      desktop_online_now: "Desktop now",
    },
    hint: {
      sessions_valid: "Non-expired sign-in sessions (session-valid) — not an online-now count.",
      ext_connected: "Users whose extension has signed in at least once — an installed but never-signed-in extension is invisible.",
      online_now: "Active within the last 5 minutes. Users with presence protection are excluded.",
    },
    funnelTitle: "Adoption funnel",
    byOrg: "By organization",
    generatedAt: "Data as of {{time}}",
  },
  revenue: {
    arr: "ARR",
    source: "Stripe · Firstier AI Governance GmbH",
    gross: "Gross",
    rangeLabel: "Date range",
    rangePreset: {
      last30: "Last 30 days",
      mtd: "Month to date",
      qtd: "Quarter to date",
      ytd: "Year to date",
      last90: "Last 90 days",
      custom: "Custom range",
    },
    snapshotHint: "Snapshot — not affected by the date range.",
    truncatedWarning: "Result truncated: the payment list hit the pagination cap — totals may under-report.",
    fxHintAed: "Converted via the USD peg (3.6725 AED/USD) on the ECB reference rate (as of {{date}}).",
    fxHint: "Converted at ECB reference rate (as of {{date}}).",
    currencySwitch: "Display currency",
    oob: "Bank transfer (out of band)",
    mrr: "MRR",
    activeSubs: "Active subscriptions",
    gross30d: "Gross — last 30 days",
    available: "Available balance",
    amount: "Amount",
    recent: "Recent payments",
  },
  seo: {
    tabUrls: "URLs",
    tabKeywords: "Keywords",
    tabQuestions: "Questions",
    trend: "Average position over time",
    avgPosition: "Avg. position",
    notRanked: "Not in top 30",
    accept: "Accept",
    proposalAccepted: "Proposal accepted as focus keyword.",
    focusPlaceholder: "Set focus keyword…",
    rankedQueryTip: "Google Search Console withholds queries below a low-impression threshold, so this can be blank.",
    searchVolumeTip: "Estimated monthly search volume from Bing Webmaster Tools.",
    searchVolumeEst: "est. (Bing)",
    countBadge: "{{count}} keywords",
    edit: "Edit",
    noFocus: "No focus set",
    slideoutTitle: "Edit SEO target",
    focusHint: "Set the deliberate focus keyword for this URL. Leave empty to clear it.",
    focusSourceLabel: "Focus source",
    focusSource: {
      agent_proposed: "Agent proposed",
      accepted: "Accepted",
      human_override: "Human override",
    },
    filterLang: "Language",
    langAll: "All languages",
    langEn: "English",
    langDe: "German",
    metaSectionTitle: "Meta tags",
    metaTitle: "Meta title",
    metaTitlePlaceholder: "Page title for search results…",
    metaDescription: "Meta description",
    metaDescriptionPlaceholder: "Summary shown under the title in search results…",
    ogImage: "OG image",
    ogImagePlaceholder: "https://…",
    ogImagePreviewAlt: "OG image preview",
    overridden: "Overridden",
    currentlyLive: "Currently live: {{value}}",
    metaApplyHint: "Unchanged fields keep the live value; an edited value is applied on the next SEO run.",
    healthTitle: "Technical-SEO health",
    healthFailures: "{{count}} failures",
    healthPages: "Pages checked",
    healthLinks: "Links checked",
    healthFailuresStat: "Failures",
    healthTarget: "Target",
    healthCrawledAt: "Last crawl: {{at}}",
    healthEmpty: "No crawl yet — the technical-SEO crawler will populate this once it runs.",
    regressionsTitle: "Regression flags",
    regressionsBadge: "{{count}} flagged",
    regressionsEmpty: "No regressions vs the prior period. 🎉",
    topQueriesTitle: "Top queries",
    topQueriesBadge: "Top {{count}}",
  },
  seoCol: {
    url: "URL",
    focus: "Focus keyword",
    rankedQuery: "Ranked query (GSC)",
    proposed: "Proposed (agent)",
    position: "Position",
    change: "Change",
    impressions: "Impressions",
    clicks: "Clicks",
    searchVolume: "Search volume",
    query: "Query",
    ctr: "CTR",
    tip: {
      url: "The page URL on the website this row tracks.",
      focus: "The focus keyword currently assigned to this page.",
      rankedQuery: "Top query the page ranks for in Google Search Console — withheld below a low-impression threshold, so it can be blank.",
      proposed: "Focus keyword proposed by the agent, pending review.",
      position: "Average Google ranking position (1 = top); lower is better.",
      change: "Position change versus the previous period.",
      impressions: "How often the page appeared in search results.",
      clicks: "Clicks from search results to the page.",
      searchVolume: "Estimated monthly search volume from Bing Webmaster Tools.",
      query: "The search query users typed.",
      ctr: "Click-through rate: clicks ÷ impressions.",
    },
  },
  keywordImport: {
    button: "Import CSV",
    title: "Import keyword CSV",
    description: "Upload an ATP or Sistrix keyword export. Rows are de-duplicated and merged into the research corpus.",
    sourceLabel: "Source format",
    sourceAuto: "Auto-detect",
    source: {
      atp_csv: "ATP (CSV)",
      sistrix_csv: "Sistrix (CSV)",
    },
    language: "Language",
    languagePlaceholder: "e.g. de",
    country: "Country",
    countryPlaceholder: "e.g. DE",
    file: "CSV file",
    choose: "Choose file…",
    noFile: "No file selected",
    import: "Import",
    success: "Imported {{upserted}} of {{parsed}} rows.",
    readError: "Could not read the file.",
    emptyFile: "The selected file is empty.",
  },
  keywords: {
    search: "Search keywords…",
    countBadge: "{{count}} keywords",
    filterLocale: "Locale",
    filterIntent: "Intent",
    filterScope: "Scope",
    scopeAll: "All keywords",
    scopeUnassigned: "Unassigned only",
    unassigned: "Unassigned",
    questionBadge: "Question",
    assignLabel: "Target URL",
    assignHint: "The page this keyword should rank for. Leave empty to unassign.",
    assignPlaceholder: "https://www.firstier.ai/…",
    answeringSection: "People-Also-Ask answering",
    answerStatusLabel: "Answer status",
    answerModeLabel: "Answer mode",
    answerModeHint: "Primary = on-page slug/title/meta; secondary = FAQ block.",
    answeredUrlLabel: "Answering URL",
    intent: {
      informational: "Informational",
      commercial: "Commercial",
      transactional: "Transactional",
      navigational: "Navigational",
    },
    answerStatus: {
      none: "Unanswered",
      planned: "Planned",
      answered: "Answered",
      not_relevant: "Not relevant",
    },
    answerMode: {
      unset: "Not set",
      slug_title_meta: "Slug / title / meta (primary)",
      faq: "FAQ block (secondary)",
    },
  },
  keywordsCol: {
    keyword: "Keyword",
    locale: "Locale",
    intent: "Intent",
    volume: "Volume",
    cpc: "CPC",
    gscPosition: "GSC position",
    assigned: "Target URL",
    topic: "Topic",
    modifier: "Modifier",
    tip: {
      keyword: "The research keyword, merged across import sources.",
      locale: "Language and country market for this keyword.",
      intent: "Search intent: informational, commercial, transactional or navigational.",
      volume: "Estimated monthly search volume (best available source).",
      cpc: "Average cost-per-click for this keyword (paid search) — used for SEA planning.",
      gscPosition: "Average Google ranking position from Search Console (1 = top); lower is better.",
      assigned: "The page this keyword is assigned to rank for.",
      topic: "The seed topic this keyword was expanded from.",
      modifier: "The question modifier (e.g. how, what, best) for this query.",
    },
  },
  questions: {
    search: "Search questions…",
    countBadge: "{{count}} questions",
    filterTopic: "Topic",
    filterStatus: "Status",
    answer: "Answer",
  },
  questionsCol: {
    question: "Question",
    status: "Answer status",
    tip: {
      question: "The People-Also-Ask style question keyword.",
      status: "Whether this question has been answered on-site: unanswered, planned or answered.",
    },
  },
  toast: {
    updated: "Saved.",
    updateError: "Could not save changes.",
    cvError: "Could not open the CV.",
  },
  col: {
    email: "Email",
    name: "Name",
    status: "Status",
    category: "Category",
    source: "Source",
    sourceCategory: "Source category",
    created: "Received",
    confirmed: "Confirmed",
    platforms: "Platforms",
    article: "Article",
    votes: "Votes",
    helpfulPct: "Helpful",
    lastVote: "Last vote",
    comment: "Comment",
    role: "Role",
    profile: "Profile",
    cv: "CV",
    notes: "Notes",
    company: "Company",
    companySize: "Size",
    useCase: "Use case",
    market: "Market",
    message: "Message",
    plan: "Plan",
    subscription: "Subscription",
    members: "Members",
    domain: "Domain",
    industry: "Industry",
    locale: "Locale",
    appId: "APP-ID",
    hrStatus: "HR status",
    rejectionOn: "Rejection on",
    synced: "Last sync",
    dossier: "Dossier",
    correspondence: "Correspondence",
  },
  list: {
    search: "Search…",
    filterStatus: "Status",
    filterCategory: "Category",
    filterSource: "Source",
    filterSourceCategory: "Source category",
    filterMarket: "Market",
    filterRole: "Position",
    filterRisk: "Risk",
    filterPlatform: "Platform",
    edit: "View / edit",
    delete: "Delete",
    deleteTitle: "Delete permanently",
    deleteDesc: "“{{label}}” will be permanently erased — this cannot be undone.",
    deleted: "Deleted.",
    countNewsletter: "{{count}} subscribers",
    countLeads: "{{count}} leads",
    countApplications: "{{count}} applications",
    countGated: "{{count}} leads",
    countDownloadNotify: "{{count}} signups",
    countHelpFeedback: "{{count}} votes",
    countHelpComments: "{{count}} comments",
    slideoutNewsletter: "Subscriber",
    slideoutLead: "Lead",
    slideoutApplication: "Application",
  },
  newsletterSource: {
    blog: "Blog",
    guide: "Guide",
    glossary: "Glossary",
    other: "Other",
  },
  helpFeedback: {
    byArticle: "By article",
    comments: "Comments",
    openArticle: "Open article",
    capWarning: "Showing the most recent 1,000 votes — older votes are not included in the aggregates.",
  },
  batch: {
    selected: "{{count}} selected",
    selectedOnPage: "{{count}} selected on this page",
    clear: "Clear",
    selectAllMatching: "Select all {{count}} matching",
    export: "Export CSV",
    delete: "Delete",
    deleteTitle: "Delete {{count}} records permanently",
    deleteDesc: "{{count}} records will be permanently erased — this cannot be undone. Type {{word}} to confirm.",
    confirmWord: "DELETE",
    deleted: "{{count}} deleted.",
    setStatus: "Set status…",
    statusUpdated: "{{count}} updated.",
    exported: "{{count}} rows exported.",
    actionError: "The bulk action failed.",
  },
  pagination: {
    prev: "Previous",
    next: "Next",
    pageOf: "Page {{page}} of {{total}}",
  },
  actions: {
    downloadCv: "Open CV",
    noCv: "No CV",
    noProfile: "—",
    notesPlaceholder: "Internal notes…",
    saveNotes: "Save",
    cancel: "Cancel",
    save: "Save",
  },
  status: {
    pending: "Pending",
    confirmed: "Confirmed",
    unsubscribed: "Unsubscribed",
    new: "New",
    screening: "Screening",
    interview: "Interview",
    offer: "Offer",
    rejected: "Rejected",
    contacted: "Contacted",
    qualified: "Qualified",
    closed: "Closed",
    active: "Active",
    trialing: "Trial",
    past_due: "Past due",
    canceled: "Canceled",
    incomplete: "Incomplete",
    none: "—",
  },
  // hr-applicants-sync v1 lifecycle (qadar-hr LIFECYCLE_STATES — contract enum, #216)
  hrStatus: {
    received: "Received",
    acknowledged: "Acknowledged",
    docs_pending: "Docs pending",
    docs_complete: "Docs complete",
    awaiting_founder_decision: "Awaiting decision",
    decided: "Decided",
    corresponded: "Corresponded",
    withdrawn: "Withdrawn",
    archived: "Archived",
    deleted: "Deleted",
  },
  hrSource: {
    careers_form: "Form",
    careers_mail: "Mail",
  },
  hrCorrespondence: {
    acknowledgment: "Acknowledgment",
    rejection: "Rejection",
    on_hold_talent_pool: "On hold (talent pool)",
    offer_announcement: "Offer announcement",
  },
  hr: {
    managedByHr: "Managed by the HR sync — status updates come from the HR system.",
    syncStale: "Last sync more than {{hours}} h ago",
    openDossier: "Open Drive dossier",
    noDossier: "No dossier link yet",
    noCorrespondence: "No correspondence yet",
    openArchivedMessage: "Open the archived message in the Drive dossier",
  },
  desc: {
    customers: "Organizations from the Shield platform.",
    users: "Operator overview of all users across every Shield organization. Use ‘Invite customer’ to onboard a new customer — the invitee becomes its first admin.",
    newsletter: "Newsletter subscribers (double opt-in).",
    applications: "Job applications from the careers pages.",
    leads: "Contact requests from the website.",
    gated: "Gated-content leads (AI Risk report) — enrichment + drip status.",
    downloadNotify: "Launch-notification signups from the /download page — who wants which platform.",
    helpFeedback: "Help-center “Was this helpful?” votes — per-article score and reader comments.",
    invites: "Pending invitations into existing organizations. Invite a user (with a role) into an existing customer org — concierge / on the customer’s behalf. Customers normally invite their own teammates inside the Shield app.",
    accessRequests: "Waitlist requests from the invite-only signup.",
    signupCodes: "Cohort signup codes — fixed org, usage limits, expiry.",
    members: "Console admins. Invite staff and manage access.",
    seo: "Keyword rankings for the website.",
    keywords: "Researched keywords (ATP / Sistrix) with canonical volume, intent and GSC position. Assign each to a target URL.",
    questions: "People-Also-Ask answering queue — set status, mode and the answering URL for each question.",
    reviews: "Mint Shield Web review-request links, export them, and track the funnel.",
    revenue: "Revenue and subscriptions from Stripe.",
    usage: "Cross-surface usage and adoption — aggregates only, no per-person presence.",
    agents: "Firstier agent platform — registry, runs, budget and the kill switch.",
  },
  review: {
    mintTitle: "Mint review invites",
    mintHint: "One recipient per line: email — or name, email, phone, locale (en/de).",
    recipients: "Recipients",
    recipientsPlaceholder: "jane@example.com\nBob, bob@example.com, +49 30 1234567, de",
    sendEmail: "Send email now",
    sendEmailHint: "Sends a transactional email to each recipient that has an email address.",
    mint: "Mint invites",
    parsedCount: "{{count}} recipients parsed",
    noRecipients: "Add at least an email or phone per line.",
    mintError: "Minting failed.",
    notConfigured: "Minting isn't configured yet (the backend secret isn't set).",
    resultTitle: "Minted ({{count}})",
    copyLinks: "Copy links",
    exportCsv: "Export CSV",
    copied: "Copied to clipboard.",
    colRecipient: "Recipient",
    colChannel: "Channel",
    colLink: "Review link",
    colVariant: "Variant",
    reusedBadge: "Reused",
    sentBadge: "Sent",
    templateTitle: "Email broadcast template",
    templateHint: "Resend broadcast HTML with a {{field}} merge field.",
    templateLocale: "Locale",
    copyTemplate: "Copy template HTML",
    templateCopied: "Template HTML copied.",
    templateError: "Could not load the template.",
    funnelTitle: "Funnel",
    ctr: "Click-through",
    total: "Total invites",
    status: { sent: "Sent", opened: "Opened", clicked: "Clicked", done: "Done", unsubscribed: "Unsubscribed" },
    invitesTitle: "Invites",
    colStatus: "Status",
    colSentAt: "Created",
    colOpenedAt: "Opened",
    colClickedAt: "Clicked",
    variantsTitle: "Variant pool",
    colAngle: "Angle",
    colLocale: "Locale",
    colUsage: "Uses",
    colActive: "Active",
    activeBadge: "Active",
    inactiveBadge: "Inactive",
    poolWarning: "Thin variant pool for {{locale}} ({{count}} active) — add more review_variants to avoid reused drafts.",
  },
  invites: {
    countBadge: "{{count}} pending",
    colEmail: "Email",
    colOrg: "Organization",
    colRole: "Role",
    colExpires: "Expires",
    colInvited: "Invited",
    revoke: "Revoke",
    revokeTitle: "Revoke invitation",
    revokeDesc: "Revoke the invitation for “{{email}}”? Its link stops working immediately.",
    revoked: "Invitation revoked.",
    resend: "Resend email",
    resent: "Invite email resent.",
    resendStub: "Email sending is pending cross-repo wiring (shield-control send-invite).",
    emailPendingBadge: "Email pending",
    arCountBadge: "{{count}} requests",
    colName: "Name",
    colCompany: "Company",
    colNote: "Note",
    colStatus: "Status",
    colRequested: "Requested",
    arStatus: { pending: "Pending", invited: "Invited", rejected: "Rejected" },
    reject: "Reject",
    requestRejected: "Request rejected.",
    invite: "Invite",
    inviteTitle: "Invite to an organization",
    inviteOrg: "Organization",
    inviteRole: "Role",
    inviteHint: "Creates an invitation into the chosen org and emails the redemption link.",
    inviteCreate: "Create invite",
    inviteCreated: "Invitation sent.",
    inviteCreatedNoEmail: "Invitation created, but the email could not be sent — use Resend.",
    alreadyInvited: "{{email}} already has a pending invitation — we refreshed it.",
    scCountBadge: "{{count}} codes",
    colCode: "Code",
    colUses: "Uses",
    colExpiry: "Expiry",
    colCreated: "Created",
    create: "New code",
    createTitle: "Create signup code",
    codeLabel: "Code",
    codePlaceholder: "COHORT-2026",
    maxUses: "Max uses",
    maxUsesHint: "Blank = unlimited.",
    expiry: "Expiry (optional)",
    createBtn: "Create code",
    codeCreated: "Signup code created.",
    revokeCode: "Revoke",
    revokeCodeTitle: "Revoke signup code",
    revokeCodeDesc: "Revoke code “{{code}}”? It can no longer be redeemed.",
    codeRevoked: "Code revoked.",
    revokedBadge: "Revoked",
    activeBadge: "Active",
    unlimited: "Unlimited",
    noExpiry: "No expiry",
    actionError: "The action failed.",
  },
  gated: {
    colCompany: "Company",
    colAsset: "Asset",
    colRisk: "Risk",
    colStatus: "Status",
    colDrip: "Drip",
    status: { pending: "Pending", confirmed: "Confirmed" },
    drip: { none: "None", scheduled: "Scheduled", stopped: "Stopped", completed: "Completed" },
    risk: { Elevated: "Elevated", High: "High", Moderate: "Moderate", Low: "Low" },
    dripAutoStart: "Auto-start drip",
    dripAutoStartHint: "When on, new confirmed leads enter the drip sequence automatically.",
    dripAutoStartSaved: "Setting saved.",
    payloadTitle: "Calculator inputs",
    enrichmentTitle: "Company enrichment",
    actionsTitle: "Actions",
    fieldAsset: "Asset",
    fieldDomain: "Domain",
    fieldCreated: "Created",
    fieldConfirmed: "Confirmed",
    fieldLocale: "Locale",
    fieldConsent: "Marketing consent",
    fieldRevenueStep: "Revenue step",
    fieldCompanySize: "Company size",
    fieldAiMaturity: "AI maturity",
    fieldRisk: "Risk level",
    fieldCompanyName: "Company name",
    fieldIndustry: "Industry",
    fieldDescription: "Description",
    openHomepage: "Homepage",
    openScreenshot: "Screenshot",
    openReport: "Report PDF",
    noEnrichment: "No enrichment data yet.",
    actionDrip: "Start drip",
    actionStopDrip: "Stop drip",
    actionResend: "Resend report",
    actionPromote: "Promote to lead",
    promoteDone: "Promoted to the sales pipeline.",
    promoteAlready: "This email is already a lead.",
    alreadyLead: "Already in the sales pipeline.",
    leadBadge: "Lead",
    dripNoConsent: "Lead has no marketing consent — drip can't be started.",
    dripDone: {
      start: "Drip sequence scheduled.",
      stop: "Drip sequence stopped.",
      resend: "Report re-sent.",
    },
  },
  orgs: {
    searchPlaceholder: "Search organizations…",
    filterPlan: "Plan",
    filterStatus: "Subscription",
    viewActive: "Active",
    viewArchived: "Archived",
    countBadge: "{{count}} organizations",
    colOrganization: "Organization",
    colPlan: "Plan",
    colSubscription: "Subscription",
    colMembers: "Members",
    colIndustry: "Industry",
    colCreated: "Created",
    edit: "Edit",
    delete: "Delete",
    restore: "Restore",
    restored: "Organization restored.",
    saved: "Organization saved.",
    deletedToast: "Organization archived. Stripe subscriptions were canceled.",
    purgedToast: "Organization permanently deleted.",
    usersLink: "Users: {{count}}",
    policiesLink: "Policies",
    slideoutTitle: "Edit organization",
    fieldName: "Name",
    fieldDomain: "Domain",
    fieldIndustry: "Industry",
    fieldPlan: "Plan",
    fieldPolicyMode: "Default policy mode",
    policyModeHint: "Takes effect immediately — this is the live Shield enforcement setting.",
    fieldShowUserList: "Show user list",
    fieldContentDetection: "Content detection",
    billingTitle: "Billing",
    billingHint: "Managed in Stripe (source of truth). Changes go through the actions below; the mirror can lag a few seconds.",
    billingPending: "Syncing…",
    billingActionOk: "Done — Stripe is syncing.",
    billingNoCustomer: "No Stripe customer linked yet.",
    billingNoSubscription: "no subscription",
    billingRenewsAt: "renews {{date}}",
    billingCancelsAt: "ends {{date}}",
    billingSeats: "{{count}} seats",
    billingInterval: "Billing interval",
    billingInterval_annual: "Annual",
    billingInterval_monthly: "Monthly",
    billingChangePlan: "Change plan",
    billingStart: "Start subscription",
    billingLinkCustomer: "Link Stripe customer",
    billingLinkPlaceholder: "cus_… (leave empty to create a new customer)",
    billingCancel: "Cancel subscription",
    billingCancelTitle: "Cancel subscription",
    billingCancelDesc: "\"{{name}}\" keeps access until {{date}}, then the subscription ends. Stripe stops billing automatically.",
    dangerZone: "Danger zone",
    selectAllMatching: "Select all {{count}} matching customers",
    allMatchingSelected: "All {{count}} matching customers selected",
    archive: "Archive",
    softDeleteTitle: "Archive organization",
    softDeleteDesc: "\"{{name}}\" is hidden from the active list and its Stripe subscriptions are canceled immediately. You can restore it within 30 days; after that it is permanently deleted.",
    bulkDeleteTitle_one: "Archive {{count}} customer?",
    bulkDeleteTitle_other: "Archive {{count}} customers?",
    bulkDeleteDesc_one: "{{count}} organization is hidden from the active list and its Stripe subscriptions are canceled immediately. You can restore it within 30 days; after that it is permanently deleted.",
    bulkDeleteDesc_other: "{{count}} organizations are hidden from the active list and their Stripe subscriptions are canceled immediately. You can restore them within 30 days; after that they are permanently deleted.",
    bulkDeletedToast_one: "{{count}} organization archived. Stripe subscriptions were canceled.",
    bulkDeletedToast_other: "{{count}} organizations archived. Stripe subscriptions were canceled.",
    purgeAction: "Delete permanently",
    purgeTitle: "Permanently delete organization",
    purgeDesc: "All data of \"{{name}}\" — policies, apps, audit history and all member accounts — is deleted permanently. This cannot be undone.",
    purgeNotSoftDeleted: "Archive the organization first — permanent deletion requires a prior archive.",
    typeToConfirm: "Type {{value}} to confirm",
    archivedBadge: "Archived",
    demoBadge: "Demo",
    domainsTitle: "Domains",
    domainVerified: "Verified",
    domainPending: "Pending",
    domainPrimary: "Primary",
    domainsBadge: "{{verified}}/{{count}} domains",
    domainsEmpty: "No domains claimed yet.",
    domainsTransferNote: "Domains are set by customer verification in Shield. To reassign a verified domain, use the staff domain-transfer op (shield-control#268).",
    growthTitle: "Customer growth",
  },
  policies: {
    pageTitle: "{{org}} · Policies",
    pageDescription: "View and edit this organization's Shield policies on its behalf.",
    new: "New policy",
    create: "Create policy",
    edit: "Edit policy",
    createDesc: "Tool-specific policies apply in addition to general ones; on conflict the stricter action wins.",
    createBtn: "Create policy",
    emptyTitle: "No policies yet",
    emptyDesc: "Create the first policy for this organization. General policies apply to all tools; tool-specific ones target individual AI apps.",
    generalSection: "General policies",
    generalSectionDesc: "Apply to all tools.",
    generalEmpty: "No general policies yet.",
    specificSection: "Tool-specific policies",
    specificSectionDesc: "Scoped to individual AI apps.",
    specificEmpty: "No tool-specific policies yet.",
    appliesTo: "Applies to",
    appliesToLabel: "Applies to:",
    allTools: "All tools",
    unknownTool: "Unknown tool",
    scopeAll: "All tools",
    scopeSpecific: "Specific tools",
    specificTools: "Tools",
    pickTools: "Select AI apps…",
    pickAtLeastOne: "Pick at least one tool.",
    precedenceHint: "Tool-specific policies apply in addition to general ones; on conflict the stricter action wins.",
    name: "Name",
    namePlaceholder: "e.g. Block source-code uploads",
    nameTooShort: "Name must be at least 2 characters.",
    descLabel: "Description",
    dataTypes: "Data types",
    actions: "Actions",
    enforcement: "Enforcement",
    pausedTag: "Paused",
    activated: "Policy activated.",
    paused: "Policy paused.",
    created: "Policy created.",
    updated: "Policy updated.",
    deleted: "Policy deleted.",
    delete: "Delete",
    deleteTitle: "Delete policy",
    deleteDesc: "Delete policy \"{{name}}\"? This removes its enforcement and cannot be undone.",
    deleteConfirm: "Delete policy",
    layoutTable: "Table view",
    layoutSections: "Section view",
    statusActive: "Active",
    statusPaused: "Paused",
    data: {
      pii: "PII (personal data)",
      phi: "PHI (health data)",
      financial: "Financial data",
      source_code: "Source code",
      contracts: "Contracts",
      secrets: "Secrets / credentials",
      piiHint: "Email, phone, national ID, tax ID. IBAN & card numbers are covered by Financial data.",
      financialHint: "IBAN, credit/debit card, BIC/SWIFT, routing numbers — not PII.",
      source_codeHint: "Code blocks, function/class definitions, script syntax.",
      secretsHint: "API keys, access tokens, JWTs, private keys.",
      unsupportedHint: "Detection coming soon.",
    },
    act: {
      prompt: "Submit prompt",
      upload: "Upload file",
      paste: "Copy / paste",
      download: "Download",
      share: "Share / open-in",
      promptHint: "Sending a chat message to the AI tool.",
      uploadHint: "Uploading a file into the AI tool.",
      pasteHint: "Pasting content into the AI tool.",
      downloadHint: "Downloading generated content.",
      shareHint: "Sharing or opening content in another app.",
    },
    enf: {
      observe: "Observe",
      warn: "Warn",
      justify: "Justify",
      transform: "Transform",
      block: "Block",
    },
    fieldTip: {
      appliesTo: "Choose whether this policy applies to all AI tools or only specific ones. Tool-specific policies stack on top of general ones; when they conflict, the stricter action wins.",
      tools: "The specific AI tools this policy targets. Only used when 'Applies to' is set to specific tools.",
      name: "A short, unique name for this policy. Auto-filled from your selection until you edit it.",
      description: "Optional context explaining what this policy does. Auto-filled from your selection until you edit it.",
      dataTypes: "The content types this policy watches for, detected on-device. Types without a detector yet are disabled.",
      actions: "The user actions this policy governs — submitting prompts, uploading files, pasting, downloading or sharing.",
      enforcement: "What happens on a match: observe (log only), warn, justify (require a reason), transform (redact) or block.",
    },
    autoName: {
      allUsage: "all AI usage",
      blockTool: "Block {{tool}}",
      enf: {
        observe: "Observe {{data}}",
        warn: "Warn on {{data}}",
        justify: "Justify {{data}}",
        transform: "Redact {{data}}",
        block: "Block {{data}}",
      },
      dt: {
        pii: "PII",
        financial: "financial data",
        source_code: "source code",
        secrets: "secrets",
        contracts: "contracts",
        phi: "PHI",
      },
    },
    autoDesc: {
      allActivity: "all AI activity",
      targetAll: "across all AI tools",
      targetTool: "in {{tool}}",
      targetTools: "in the selected tools",
      blockTool: "Blocks all use of {{tool}}.",
      enf: {
        observe: "Logs {{data}} {{target}}.",
        warn: "Warns on {{data}} {{target}}.",
        justify: "Requires justification for {{data}} {{target}}.",
        transform: "Redacts {{data}} {{target}}.",
        block: "Blocks {{data}} {{target}}.",
      },
    },
  },
  policiesTable: {
    search: "Search policies…",
    filterEnforcement: "Enforcement",
    filterStatus: "Status",
    columns: "Columns",
    emptyTitle: "No policies yet",
    emptyDesc: "Create your first policy for this customer.",
    noHitsTitle: "No policies found",
    noHitsDesc: "No policies match your filters. Clear them or create a new policy.",
    clearFilters: "Clear filters",
    exportCsv: "Export CSV",
    exported: "Exported {{count}} policies.",
    col: {
      name: "Name",
      enforcement: "Enforcement",
      applies: "Applies to",
      data: "Data types",
      actions: "Actions",
      status: "Status",
      version: "Version",
      created: "Created",
      modified: "Last modified",
    },
    tip: {
      name: "The policy name and its description.",
      enforcement: "How the policy acts when it matches: observe, warn, justify, transform or block.",
      applies: "Which AI tools the policy targets — all tools, or specific ones.",
      data: "Data types the policy governs (e.g. PII, secrets, source code).",
      actions: "User actions the policy governs (e.g. prompt, upload, paste).",
      status: "Whether the policy is currently active or paused.",
      version: "Increments each time the policy is edited.",
      created: "When the policy was first created.",
      modified: "When the policy was last edited.",
    },
  },
  users: {
    countBadge: "{{count}} users",
    filterOrg: "Organization",
    filterRole: "Role",
    colOrg: "Organization",
    invite: "Invite customer",
    addUser: "Add user",
  },
  addUser: {
    title: "Add user",
    hint: "Invite into an existing organization, or create a new customer org.",
    org: "Organization",
    orgPlaceholder: "Pick an organization…",
    newOrg: "➕ New organization",
    demoBadge: "Demo",
    industry: "Industry (optional)",
    plan: "Plan",
    locale: "Invitation language",
    submit: "Add",
    button: "Add user",
  },
  industry: {
    label: "Industry",
    placeholder: "Search industries…",
    otherLabel: "Industry (other)",
    otherPlaceholder: "Type the industry…",
    technology_software: "Technology / Software",
    financial_services: "Financial Services / Banking",
    insurance: "Insurance",
    healthcare: "Healthcare",
    pharma_biotech: "Pharmaceuticals / Biotech",
    manufacturing: "Manufacturing",
    retail_ecommerce: "Retail / E-commerce",
    professional_services: "Professional Services / Consulting",
    legal: "Legal",
    media_entertainment: "Media / Entertainment",
    telecommunications: "Telecommunications",
    energy_utilities: "Energy / Utilities",
    real_estate: "Real Estate",
    transportation_logistics: "Transportation / Logistics",
    travel_hospitality: "Travel / Hospitality",
    education: "Education",
    government_public: "Government / Public Sector",
    nonprofit: "Non-profit / NGO",
    automotive: "Automotive",
    construction_engineering: "Construction / Engineering",
    aerospace_defense: "Aerospace / Defense",
    agriculture: "Agriculture",
    marketing_advertising: "Marketing / Advertising",
    other: "Other",
  },
  inviteCustomer: {
    title: "Invite a customer",
    description:
      "We'll email them a secure sign-in link. On first sign-in we create their organization and make them its admin.",
    emailLabel: "Customer email",
    orgLabel: "Organization name",
    orgHint: "Prefilled from the email domain — edit if needed.",
    orgPlaceholder: "Acme Inc.",
    submit: "Send invite",
    sent: "Invitation sent to {{email}}.",
    alreadyRegistered: "That email already has an account.",
    error: "Could not send the invitation.",
    errorDetail: "Could not send the invitation: {{detail}}",
  },
  orgUsers: {
    title: "Users — {{org}}",
    description: "Members of this organization.",
    searchPlaceholder: "Search users…",
    colUser: "User",
    colRole: "Role",
    colLocale: "Locale",
    colJoined: "Joined",
    colLastSignIn: "Last sign-in",
    role: { admin: "Admin", security: "Security", auditor: "Auditor", member: "Member", none: "—" },
    slideoutTitle: "Edit user",
    fieldFullName: "Full name",
    fieldRole: "Role",
    fieldEmail: "Email",
    saved: "User saved.",
    deleteTitle: "Delete user account",
    deleteDesc: "\"{{email}}\" loses access permanently: profile, role and the login account are deleted. This cannot be undone.",
    deletedToast: "User account deleted.",
    backToCustomers: "All customers",
  },
  settings: {
    title: "Settings",
    description: "Manage your account.",
    tabs: { profile: "Profile", security: "Security", preferences: "Preferences" },
    name: "Display name",
    nameDesc: "Shown across the console for your account.",
    namePlaceholder: "Your name",
    email: "Email",
    emailDesc: "The address you sign in with.",
    emailChangeHint: "Change your email under the Security tab.",
    save: "Save",
    saved: "Saved.",
    changeEmail: "Change email",
    changeEmailDesc: "We'll send a confirmation link to the new address before it takes effect.",
    newEmail: "New email",
    sendConfirmation: "Send confirmation",
    emailChangeSent: "Confirmation sent. Check the new inbox to finish the change.",
    changePassword: "Change password",
    changePasswordDesc: "Use at least 8 characters.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords do not match.",
    updatePassword: "Update password",
    passwordUpdated: "Password updated.",
    signOutAll: "Sign out everywhere",
    signOutAllDesc: "End all sessions on every device, including this one.",
    signOutAllBtn: "Sign out all sessions",
    language: "Language",
    languageDesc: "Console language for your account.",
    errorGeneric: "Something went wrong. Please try again.",
  },
  members: {
    invite: "Invite",
    inviteLabel: "Invite a teammate by email",
    invited: "Invitation sent.",
    inviteError: "Could not send the invitation.",
    emailNotAllowed: "Only Firstier staff addresses can be invited.",
    statusInvited: "Invited",
    statusActive: "Active",
    lastSignIn: "Last sign-in",
    remove: "Remove",
    removed: "Member removed.",
    removeError: "Could not remove the member.",
    you: "You",
  },
  placeholder: {
    comingSoon: "Coming soon",
    description: "This area is part of the console scaffold and will be wired to live data next.",
  },
  error: {
    title: "Something went wrong",
    description: "An unexpected error occurred while rendering this page.",
    technical: "Technical details",
    goHome: "Go to dashboard",
    tryAgain: "Try again",
  },
  notFound: {
    title: "Page not found",
    description: "The page you are looking for does not exist.",
    goHome: "Go to dashboard",
  },
  playground: {
    title: "Data Types",
    copy: "Copy prompt",
    layoutFull: "Full view",
    layoutSimple: "Simple view",
    simple: {
      heading: "Example prompt",
    },
    description: "Demo and test prompts for the Shield data-type detectors. Prompts are copy-paste material — verified against the Shield Web detectors.",
    nav: {
      label: "Sections",
      combined: "Combined generator",
    },
    combined: {
      heading: "Combined generator",
      description: "Pick the data types to include; the prompt below is generated deterministically with one fragment per selected type. Copy it straight into a chat to test detection.",
    },
    status: {
      live: "Live (v0.1.8)",
      preview: "v0.1.11 (CWS review)",
      backlog: "No detector yet (backlog #312)",
    },
    type: {
      pii: "PII",
      financial: "Financial",
      secrets: "Secrets",
      source_code: "Source code",
      phi: "PHI",
      contracts: "Contracts",
    },
    desc: {
      pii: "Anchors on email, phone (with +/() shape), US SSN (123-45-6789 shape) and German Steuer-ID (MOD 11,10 checksum). Recommended enforcement: transform (mask / pseudonymise).",
      financial: "Checksum-gated: IBAN (mod-97), credit/debit card (Luhn), BIC/SWIFT structure and US ABA routing (mod-10). Recommended enforcement: transform.",
      secrets: "Regex for API-key shapes (sk-…, AKIA…), JWTs and PEM private-key blocks. Recommended enforcement: transform (redact before send).",
      source_code: "Heuristic — fires on a fenced code block or ≥3 code keywords/braces. Recommended enforcement: block or warn (transform never redacts code).",
      phi: "No detector yet (backlog #312) — health data such as diagnoses, HbA1c/labs and medications will NOT be caught today.",
      contracts: "No detector yet (backlog #312) — contractual and legal clauses will NOT be caught today.",
    },
    example: {
      pii1: "Email + phone",
      pii2: "SSN + tax ID",
      pii3: "Mixed",
      financial1: "IBAN",
      financial2: "Card + BIC",
      financial3: "Routing + mixed",
      secrets1: "API keys",
      secrets2: "JWT",
      secrets3: "Private key",
      sourceJs: "JavaScript",
      sourcePython: "Python",
      sourceSql: "SQL",
      phi1: "Diagnosis",
      phi2: "Discharge",
      phi3: "Lab result",
      contracts1: "NDA",
      contracts2: "Termination",
      contracts3: "Liability",
    },
  },
} as const;

export default en;
